// Pure geometry for the air layer (no JSX/leaflet): route, traveled part, marker
// position and heading all come from ONE path so they always agree.
import greatCircle from '@turf/great-circle';

const RAD = Math.PI / 180;
// Enough for a smooth arc without piling vertices ~4px apart (Leaflet rounds each
// vertex to an integer pixel, which shows as wobble on a dense, un-simplified line).
const NPOINTS = 56;

const num = (v) => (v == null || v === '' ? NaN : Number(v));
const validPoint = (lat, lng) => {
  const a = num(lat);
  const b = num(lng);
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180
    ? [a, b]
    : null;
};

const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (Math.max(-85, Math.min(85, lat)) * RAD) / 2));

function haversineKm([lat1, lng1], [lat2, lng2]) {
  const dLat = (lat2 - lat1) * RAD;
  const dLng = (lng2 - lng1) * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * RAD) * Math.cos(lat2 * RAD) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** On-screen (Web-Mercator) bearing of a->b, degrees clockwise from north. */
function screenBearing(a, b) {
  const dx = (b[1] - a[1]) * RAD;
  const dy = mercY(b[0]) - mercY(a[0]);
  if (dx === 0 && dy === 0) return 0;
  return (Math.atan2(dx, dy) / RAD + 360) % 360;
}

/** Linear interpolation in projected space so the point sits on the drawn line. */
function lerpProjected(a, b, f) {
  const y = mercY(a[0]) + (mercY(b[0]) - mercY(a[0])) * f;
  return [(2 * Math.atan(Math.exp(y)) - Math.PI / 2) / RAD, a[1] + (b[1] - a[1]) * f];
}

/**
 * Turf's own split can miss (e.g. LIM -> SIN). Whatever it returns, no polyline
 * may jump across the date line: cut at +-180 and continue on the other side.
 */
function splitAtDateLine(seg) {
  const out = [];
  let cur = [seg[0]];
  for (let i = 1; i < seg.length; i++) {
    const a = seg[i - 1];
    const b = seg[i];
    if (Math.abs(b[1] - a[1]) > 180) {
      const edge = a[1] > 0 ? 180 : -180;
      const bUnwrapped = b[1] + (a[1] > 0 ? 360 : -360);
      const t = (edge - a[1]) / (bUnwrapped - a[1]);
      const lat = a[0] + t * (b[0] - a[0]);
      cur.push([lat, edge]);
      out.push(cur);
      cur = [[lat, -edge], b];
    } else {
      cur.push(b);
    }
  }
  out.push(cur);
  return out.filter((s) => s.length >= 2);
}

function progressOf(row) {
  if (String(row?.status || '').toLowerCase() === 'arrived') return 100;
  const p = num(row?.transit_percentage);
  return Number.isFinite(p) ? Math.max(0, Math.min(100, p)) : 0;
}

const EMPTY = { segments: [], traveledSegments: [], remainingSegments: [], position: null, heading: 0 };

export function buildAirRoute(row) {
  const origin = validPoint(row?.origin_lat, row?.origin_lng);
  const dest = validPoint(row?.dest_lat, row?.dest_lng);
  if (!origin || !dest) return EMPTY;
  if (origin[0] === dest[0] && origin[1] === dest[1]) return EMPTY;

  let segments;
  try {
    const f = greatCircle([origin[1], origin[0]], [dest[1], dest[0]], { npoints: NPOINTS, offset: 10 });
    const raw = f.geometry.type === 'MultiLineString' ? f.geometry.coordinates : [f.geometry.coordinates];
    // One ordered sample list, then ONE splitter: this also repairs the gap turf
    // leaves between its own segments at the date line.
    const samples = raw.flat().map(([lng, lat]) => [lat, lng]);
    segments = (samples.length >= 2 ? splitAtDateLine(samples) : [])
      .filter((seg) => seg.length >= 2 && seg.every(([la, lo]) => Number.isFinite(la) && Number.isFinite(lo)));
  } catch {
    return EMPTY;
  }
  if (!segments.length) return EMPTY;

  const pct = progressOf(row);
  const segLens = segments.map((seg) => seg.slice(1).reduce((s, pt, i) => s + haversineKm(seg[i], pt), 0));
  const total = segLens.reduce((a, b) => a + b, 0);
  const lastSeg = segments[segments.length - 1];

  if (pct <= 0 || total === 0) {
    return { segments, traveledSegments: [], remainingSegments: segments, position: segments[0][0], heading: screenBearing(segments[0][0], segments[0][1]) };
  }
  if (pct >= 100) {
    return {
      segments,
      traveledSegments: segments,
      remainingSegments: [],
      position: lastSeg[lastSeg.length - 1],
      heading: screenBearing(lastSeg[lastSeg.length - 2], lastSeg[lastSeg.length - 1]),
    };
  }

  let remaining = (pct / 100) * total;
  const traveledSegments = [];
  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    if (remaining >= segLens[s]) {
      traveledSegments.push(seg);
      remaining -= segLens[s];
      continue;
    }
    const part = [seg[0]];
    for (let i = 1; i < seg.length; i++) {
      const d = haversineKm(seg[i - 1], seg[i]);
      if (remaining > d) {
        part.push(seg[i]);
        remaining -= d;
        continue;
      }
      const cut = lerpProjected(seg[i - 1], seg[i], d === 0 ? 0 : remaining / d);
      part.push(cut);
      traveledSegments.push(part);
      return {
        segments,
        traveledSegments,
        remainingSegments: [[cut, ...seg.slice(i)], ...segments.slice(s + 1)],
        position: cut,
        heading: screenBearing(seg[i - 1], seg[i]) };
    }
    traveledSegments.push(part);
    break;
  }
  const end = traveledSegments[traveledSegments.length - 1];
  return {
    segments,
    traveledSegments,
    remainingSegments: [],
    position: end[end.length - 1],
    heading: screenBearing(end[end.length - 2] || end[0], end[end.length - 1]),
  };
}
